import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const ProviderGalleryCarousel = ({ provider }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const images = provider.gallery || [];

  const selectImage = (nextIndex) => {
    setActiveIndex(nextIndex);
  };

  const showPrevious = () => {
    setActiveIndex((currentIndex) =>
      currentIndex === 0 ? images.length - 1 : currentIndex - 1
    );
  };

  const showNext = () => {
    setActiveIndex((currentIndex) =>
      currentIndex === images.length - 1 ? 0 : currentIndex + 1
    );
  };

  const handlePreviousClick = (event) => {
    if (event.detail === 0) {
      showPrevious();
    }
  };

  const handleNextClick = (event) => {
    if (event.detail === 0) {
      showNext();
    }
  };

  return (
    <section data-testid="provider-gallery">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-sky-600">
            Hospital gallery
          </p>
          <h2 className="font-display text-xl font-bold text-[#06152D] sm:text-2xl">
            Inside {provider.name}
          </h2>
        </div>
        <p
          className="shrink-0 text-sm font-semibold text-slate-500"
          data-testid="provider-gallery-slide-count"
        >
          {activeIndex + 1} / {images.length}
        </p>
      </div>

      <div
        aria-label={`${provider.name} photo carousel`}
        aria-roledescription="carousel"
        className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-slate-100"
        data-testid="provider-gallery-carousel"
        role="region"
      >
        <img
          key={images[activeIndex]}
          alt={`${provider.name} gallery image ${activeIndex + 1}`}
          className="absolute inset-0 h-full w-full animate-in fade-in duration-300 object-cover"
          data-testid={`provider-gallery-image-${activeIndex + 1}`}
          src={images[activeIndex]}
        />

        <button
          aria-label="Show previous hospital photo"
          className="absolute left-4 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center
            rounded-full border border-white/70 bg-white/90 text-[#06152D] shadow-lg
            transition-colors duration-200 hover:bg-white focus:outline-none focus:ring-2
            focus:ring-sky-400"
          data-cursor="PREV"
          data-testid="provider-gallery-previous-button"
          onClick={handlePreviousClick}
          onPointerDown={showPrevious}
          type="button"
        >
          <ChevronLeft aria-hidden="true" className="pointer-events-none" size={20} />
        </button>
        <button
          aria-label="Show next hospital photo"
          className="absolute right-4 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center
            rounded-full border border-white/70 bg-white/90 text-[#06152D] shadow-lg
            transition-colors duration-200 hover:bg-white focus:outline-none focus:ring-2
            focus:ring-sky-400"
          data-cursor="NEXT"
          data-testid="provider-gallery-next-button"
          onClick={handleNextClick}
          onPointerDown={showNext}
          type="button"
        >
          <ChevronRight aria-hidden="true" className="pointer-events-none" size={20} />
        </button>
      </div>

      <div
        className="mt-3 grid grid-cols-5 gap-2"
        data-testid="provider-gallery-thumbnails"
      >
        {images.map((src, index) => (
          <button
            key={src}
            aria-label={`Show hospital photo ${index + 1}`}
            aria-pressed={activeIndex === index}
            className={`aspect-[4/3] overflow-hidden rounded-lg border-2 transition-opacity
              duration-200 ${
                activeIndex === index
                  ? "border-sky-500 opacity-100"
                  : "border-transparent opacity-60 hover:opacity-100"
              }`}
            data-cursor="VIEW"
            data-testid={`provider-gallery-thumbnail-${index + 1}`}
            onClick={() => selectImage(index)}
            type="button"
          >
            <img alt="" className="h-full w-full object-cover" src={src} />
          </button>
        ))}
      </div>
    </section>
  );
};